import { formatMoney } from './currency';
import { isFreeListingDisplay } from './price';
import {
  getLowestSubscriptionPlan,
  isContentSubscriptionListing,
  subscriptionIntervalMessageId,
} from './digitalSubscription';
import { types as sdkTypes } from './sdkLoader';
import { LISTING_STATE_PUBLISHED } from './types';
import { LISTING_PAGE_PARAM_TYPES, LISTING_PAGE_PARAM_TYPE_EDIT } from './urlHelpers';
import { isUuid } from './uuid';

const { Money } = sdkTypes;

export const MAX_PACKAGE_ITEMS = 10;
export const PACKAGE_NAME_MAX = 40;
/** One-line subtitle on the public picker; keep short enough to scan. */
export const PACKAGE_DESCRIPTION_MAX = 80;

let fallbackPackageSeq = 0;

export const createPackageId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  fallbackPackageSeq += 1;
  const tail = `${Date.now().toString(16)}${fallbackPackageSeq.toString(16)}`
    .padStart(12, '0')
    .slice(-12);
  return `00000000-0000-4000-8000-${tail}`;
};

const trimString = value => (typeof value === 'string' ? value.trim() : '');

const normalizeItem = item => {
  if (!item || typeof item !== 'object') {
    return null;
  }
  const listingId = isUuid(item.listingId) ? item.listingId : null;
  const id = isUuid(item.id) ? item.id : null;
  if (!listingId || !id) {
    return null;
  }
  return {
    id,
    listingId,
    name: trimString(item.name),
    description: trimString(item.description),
  };
};

export const normalizePackages = raw => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const groupId = isUuid(raw.groupId) ? raw.groupId : null;
  if (!groupId || !Array.isArray(raw.items)) {
    return null;
  }
  const items = raw.items.map(normalizeItem).filter(Boolean);
  if (items.length === 0) {
    return null;
  }
  return { groupId, items, enabled: raw.enabled !== false };
};

export const isCompletePackageItem = item =>
  !!(
    item &&
    isUuid(item.id) &&
    isUuid(item.listingId) &&
    trimString(item.name) &&
    trimString(item.description)
  );

export const validatePackageItems = (items, { currentListingId, max = MAX_PACKAGE_ITEMS } = {}) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) {
    return { valid: true, error: null };
  }
  if (list.length > max) {
    return { valid: false, error: 'max' };
  }
  if (!list.every(isCompletePackageItem)) {
    return { valid: false, error: 'incomplete' };
  }
  if (list.length < 2) {
    return { valid: false, error: 'incomplete' };
  }
  const listingIds = list.map(item => item.listingId);
  if (new Set(listingIds).size !== listingIds.length) {
    return { valid: false, error: 'duplicate' };
  }
  const currentCount = listingIds.filter(id => id === currentListingId).length;
  if (currentCount !== 1) {
    return { valid: false, error: 'current' };
  }
  return { valid: true, error: null };
};

export const selectedPackage = (items, listingId) =>
  (items || []).find(item => item.listingId === listingId) || null;

export const listingIdFromEntity = listing => listing?.id?.uuid || listing?.id || null;

export const publishedMembers = (items, listingsById = {}) =>
  (items || []).filter(item => {
    const listing = listingsById[item.listingId];
    return listing?.attributes?.state === LISTING_STATE_PUBLISHED;
  });

export const siblingListingIds = (items, currentId) =>
  (items || []).map(item => item.listingId).filter(id => id && id !== currentId);

export const listingInOtherFamily = (listing, groupId) => {
  const pkgs = normalizePackages(listing?.attributes?.publicData?.packages);
  if (!pkgs?.groupId) {
    return false;
  }
  if (!groupId) {
    return true;
  }
  return pkgs.groupId !== groupId;
};

export const packagesForEditor = listing => {
  const pkgs = normalizePackages(listing?.attributes?.publicData?.packages);
  const currentId = listingIdFromEntity(listing);
  if (!pkgs || !currentId) {
    return null;
  }
  if (!pkgs.items.some(item => item.listingId === currentId)) {
    return null;
  }
  return pkgs;
};

/**
 * Public picker is shown only when two or more members are published.
 * listingsById must include the opened listing (and prefetched siblings).
 */
export const hasPublicPackageFamily = (listing, listingsById = {}) => {
  const pkgs = normalizePackages(listing?.attributes?.publicData?.packages);
  if (!pkgs || pkgs.enabled === false) {
    return false;
  }
  const byId = { ...listingsById };
  const currentId = listingIdFromEntity(listing);
  if (currentId && !byId[currentId]) {
    byId[currentId] = listing;
  }
  return publishedMembers(pkgs.items, byId).length >= 2;
};

export const packagesPayloadFromItems = (items, { previousGroupId, enabled = true } = {}) => {
  const complete = (items || []).filter(isCompletePackageItem);
  if (complete.length < 2) {
    return null;
  }
  return {
    groupId: isUuid(previousGroupId) ? previousGroupId : createPackageId(),
    enabled: enabled !== false,
    items: complete.map(item => ({
      id: item.id,
      listingId: item.listingId,
      name: trimString(item.name).slice(0, PACKAGE_NAME_MAX),
      description: trimString(item.description).slice(0, PACKAGE_DESCRIPTION_MAX),
    })),
  };
};

export const buildPackageSync = ({ previousPackages, nextPackages, currentListingId } = {}) => {
  if (!nextPackages && !previousPackages) {
    return null;
  }
  const previousItemIds = (previousPackages?.items || [])
    .map(pkgItem => pkgItem.listingId)
    .filter(Boolean);
  const nextItemIds = (nextPackages?.items || []).map(pkgItem => pkgItem.listingId);
  return {
    packages: nextPackages || null,
    siblingListingIds: nextItemIds.filter(id => id !== currentListingId),
    removedListingIds: previousItemIds.filter(id => !nextItemIds.includes(id)),
  };
};

const asMoney = value => {
  if (value instanceof Money) {
    return value;
  }
  if (value && typeof value.amount === 'number' && typeof value.currency === 'string') {
    return new Money(value.amount, value.currency);
  }
  return null;
};

export const packagePriceLabel = (listing, intl, config) => {
  if (!listing || !intl) {
    return '—';
  }
  try {
    if (isContentSubscriptionListing(listing)) {
      const lowest = getLowestSubscriptionPlan(listing);
      const money = asMoney(lowest);
      if (!money) {
        return '—';
      }
      const formatted = formatMoney(intl, money);
      const interval = intl.formatMessage({ id: subscriptionIntervalMessageId(lowest.interval) });
      return `${formatted} / ${interval}`;
    }

    if (isFreeListingDisplay(listing)) {
      return intl.formatMessage({ id: 'OrderPanel.listingPriceFree' });
    }

    const listingType = listing?.attributes?.publicData?.listingType;
    const listingTypeConfig = config?.listing?.listingTypes?.find(
      conf => conf.listingType === listingType
    );
    if (listingTypeConfig?.defaultListingFields?.price === false) {
      return intl.formatMessage({ id: 'OrderPanel.listingPriceFree' });
    }

    const money = asMoney(listing?.attributes?.price);
    if (!money) {
      return '—';
    }
    return formatMoney(intl, money);
  } catch (e) {
    return '—';
  }
};

const PACKAGE_RETURN_TAB_DEFAULT = 'pricing';

/**
 * Query used when a package row opens another listing in the editor.
 * After save, the wizard returns here and reloads the original pricing page.
 */
export const packageReturnQueryFromParams = params => {
  if (!params?.id) {
    return null;
  }
  return {
    packageReturnId: params.id,
    packageReturnSlug: params.slug || 'no-slug',
    packageReturnType: params.type || LISTING_PAGE_PARAM_TYPE_EDIT,
    packageReturnTab: params.tab || PACKAGE_RETURN_TAB_DEFAULT,
  };
};

export const pickPackageReturnQuery = search => {
  const id = search?.packageReturnId;
  if (!id) {
    return null;
  }
  return {
    packageReturnId: id,
    packageReturnSlug: search.packageReturnSlug || 'no-slug',
    packageReturnType: search.packageReturnType || LISTING_PAGE_PARAM_TYPE_EDIT,
    packageReturnTab: search.packageReturnTab || PACKAGE_RETURN_TAB_DEFAULT,
  };
};

export const editListingParamsFromPackageReturn = search => {
  const query = pickPackageReturnQuery(search);
  if (!query || !isUuid(query.packageReturnId)) {
    return null;
  }
  const type = LISTING_PAGE_PARAM_TYPES.includes(query.packageReturnType)
    ? query.packageReturnType
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  return {
    id: query.packageReturnId,
    slug: query.packageReturnSlug,
    type,
    tab: query.packageReturnTab || PACKAGE_RETURN_TAB_DEFAULT,
  };
};
